/**
 * The only transport code in the app: `fetch`, same-origin, JSON in and out.
 *
 * Split out of `httpRepository.ts` so each area of the app can keep its own
 * HTTP half (`scheduleHttp.ts`, `inventoryHttp.ts`) without a second copy of
 * the error handling.
 */

export class HttpError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    // The status is in the message on purpose: the query client's retry rule
    // reads it, and an Error that has lost its status is one nothing can decide
    // anything about.
    super(`${status}: ${message}`)
  }
}

export async function get<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    // The session cookie is httpOnly, so it only travels if we say so.
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string }
    throw new HttpError(response.status, body.error ?? response.statusText)
  }
  return response.json() as Promise<T>
}

export async function post<T>(path: string, body: unknown, method: 'POST' | 'PATCH' = 'POST'): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { error?: string }
    // A 409 is a *considered* refusal — the position is occupied, the part has
    // not been torn down. The server's sentence is the useful one, so it is
    // carried through verbatim rather than replaced with a generic message.
    throw new HttpError(response.status, payload.error ?? response.statusText)
  }
  return response.json() as Promise<T>
}

